import { useMutation, type UseMutationOptions, type UseMutationResult } from '@tanstack/react-query';
import jwtDecode from 'jwt-decode';
import { useNavigate } from 'react-router-dom';

import { Step } from '../App';
import { requireEnv } from '../config/env';
import {
	type AuthenticateRequest,
	type AuthenticateResponse,
	doRefreshToken as doRefreshTokenFromAuth,
	PythonRequestError,
} from '../public-api/auth';
import { getHasUserAcceptedTerms } from './terms-and-conditions.api';

const AUTH_TOKEN_URL = requireEnv(process.env.AUTH_TOKEN_URL, 'AUTH_TOKEN_URL');
const APP_URL = requireEnv(process.env.APP_URL, 'APP_URL');
const PORTAL_URL = requireEnv(process.env.PORTAL_URL, 'PORTAL_URL');
const LOGIN_URL = requireEnv(process.env.LOGIN_URL, 'LOGIN_URL');

export { PythonRequestError };
export type { AuthenticateRequest, AuthenticateResponse };

export const useDoRefreshToken = (
	options?: UseMutationOptions<AuthenticateResponse, PythonRequestError, { refreshToken: string }>
): UseMutationResult<AuthenticateResponse, PythonRequestError, { refreshToken: string }, unknown> => {
	return useMutation<AuthenticateResponse, PythonRequestError, { refreshToken: string }, unknown>({
		mutationKey: ['refresh-token'],
		mutationFn: ({ refreshToken }) => doRefreshTokenFromAuth(AUTH_TOKEN_URL, refreshToken),
		...options,
	});
};

type HandlePostAuthParams = {
	accessToken: string;
	refreshToken: string;
	isMobileApp: boolean | undefined;
	redirectUri: string | undefined;
	navigateTo: ReturnType<typeof useNavigate>;
	addRecentWorkspace: (workspaceId: string) => void;
	clearStorage: () => void;
};

export const handlePostAuthentication = async ({
	accessToken,
	refreshToken,
	isMobileApp,
	redirectUri,
	navigateTo,
	addRecentWorkspace,
	clearStorage,
}: HandlePostAuthParams) => {
	const decodedToken = jwtDecode<{ workspace: string; sub: string; scope: string }>(accessToken);

	addRecentWorkspace(decodedToken.workspace);

	const hasUserAcceptedTerms = await getHasUserAcceptedTerms(decodedToken.sub, accessToken);

	if (!hasUserAcceptedTerms) {
		navigateTo(Step.ACCEPT_TERMS_OF_USE, {
			state: {
				accessToken,
				refreshToken,
				userId: decodedToken.sub,
				workspaceId: decodedToken.workspace,
				redirectUri,
				mobile: isMobileApp,
			},
		});
		return;
	}

	// Check if we need to redirect to the portal
	const isPortalRedirect =
		Boolean(PORTAL_URL && redirectUri?.startsWith(PORTAL_URL)) ||
		(decodedToken.scope.includes('booking_portal') && !decodedToken.scope.includes('workspace'));

	redirectToApplication({
		accessToken,
		refreshToken,
		redirectUri,
		workspaceId: decodedToken.workspace,
		clearStorage,
		mode: isMobileApp ? 'mobile' : isPortalRedirect ? 'portal' : 'desktop',
	});
};

export const redirectToApplication = ({
	accessToken,
	refreshToken,
	workspaceId,
	redirectUri,
	clearStorage,
	mode = 'desktop',
}: {
	accessToken: string;
	refreshToken: string;
	workspaceId: string;
	redirectUri?: string;
	clearStorage: () => void;
	mode: 'desktop' | 'mobile' | 'portal';
}) => {
	let url: URL;
	const baseUrl = mode === 'portal' && PORTAL_URL ? PORTAL_URL : APP_URL;
	if (mode === 'mobile') {
		url = new URL(`${LOGIN_URL}/mobile.html`);
	} else if (mode === 'portal') {
		url = new URL(redirectUri?.includes(baseUrl) ? redirectUri : `${baseUrl}/${workspaceId}`);
	} else {
		url = new URL(`${baseUrl}/${workspaceId}`);
		if (redirectUri) {
			url.searchParams.append('redirect_uri', redirectUri);
		}
	}

	url.hash = new URLSearchParams({
		access_token: accessToken,
		session_token: refreshToken,
	}).toString();

	clearStorage();
	window.location.replace(url);
};
